import React from 'react';
import { observer } from 'mobx-react-lite';
import {
    ContextualMenu,
    ContextualMenuItemType,
    DirectionalHint,
    IContextualMenuItem,
} from '@fluentui/react/lib/ContextualMenu';
import SonoraResultsDetailsMenuItem from './SonoraResultsDetailsMenuItem';
import { default as loc, isCurrentCultureRightToLeft } from 'owa-localize';
import {
    ResultItemDetailValue,
    SonoraResultDetailItemsType,
    SonoraViewState,
} from 'owa-editor-sonora-types';
import {
    SonoraResultDetailItemsEmailsLabel,
    SonoraResultDetailItemsPhonesLabel,
    SonoraResultDetailItemsAddressesLabel,
} from './SonoraResultsDetailsItem.locstring.json';
import {
    hideDetailsMenu,
    clearSonoraResultItemDetails,
} from '../mutators/sonoraResultDetailsMutators';

export default observer(function SonoraResultsDetailsItem({
    resultItemDetails,
    resultItemOnClickAction,
    target,
    viewState,
}: {
    resultItemDetails: Map<SonoraResultDetailItemsType, ResultItemDetailValue[]>;
    resultItemOnClickAction: (primaryText: string) => void;
    target: HTMLElement;
    viewState: SonoraViewState;
}) {
    function getMenuHeaderLabel(itemType: SonoraResultDetailItemsType): string {
        switch (itemType) {
            case SonoraResultDetailItemsType.Emails:
                return SonoraResultDetailItemsEmailsLabel;
            case SonoraResultDetailItemsType.Phones:
                return SonoraResultDetailItemsPhonesLabel;
            case SonoraResultDetailItemsType.Addresses:
                return SonoraResultDetailItemsAddressesLabel;
            default:
                return '';
        }
    }

    function getSubMenuProps(
        resultItemDetails: Map<SonoraResultDetailItemsType, ResultItemDetailValue[]>,
        resultItemOnClickAction: (primaryText: string) => void
    ): IContextualMenuItem[] {
        let subItems: IContextualMenuItem[] = [];

        resultItemDetails.forEach((value, key) => {
            subItems.push({
                key: key,
                itemType: ContextualMenuItemType.Header,
                text: loc(getMenuHeaderLabel(key)),
            });

            value.forEach(detail => {
                subItems.push({
                    key: detail.primaryText,
                    onRender: () => (
                        <SonoraResultsDetailsMenuItem
                            item={detail}
                            resultItemOnClickAction={resultItemOnClickAction}
                        />
                    ),
                });
            });
        });
        return subItems;
    }

    function onKeyDown(event: KeyboardEvent) {
        if (event.key === 'ArrowLeft') {
            hideDetailsMenu(viewState);
            clearSonoraResultItemDetails(viewState);
        }
    }

    // TODO(zesu): The eventlistener for key down cannot be triggered by adding onKeyDown event or onItemClick attribute of ContextualMenu.
    // Also, it cannot fire the event on the SonoraPlugin, will investigate more on this.

    React.useEffect(() => {
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
        };
    }, []);

    return (
        <ContextualMenu
            items={getSubMenuProps(resultItemDetails, resultItemOnClickAction)}
            target={target}
            shouldFocusOnMount={true}
            directionalHint={
                isCurrentCultureRightToLeft()
                    ? DirectionalHint.leftTopEdge
                    : DirectionalHint.rightTopEdge
            }
        />
    );
});
